import { useUploadStore, type ActiveUpload } from '@/shared/stores/upload-store';
import { useUploadHistoryStore, type UploadAttempt } from '@/shared/stores/upload-history-store';
import { deriveUploadState } from './upload-state';

const BYTES_PER_MIB = 1_048_576;

export interface EntryChange {
  prev: ActiveUpload | undefined;
  next: ActiveUpload;
  attempt: UploadAttempt | undefined;
  now: number;
}

function seedAttempt(entry: ActiveUpload, now: number): UploadAttempt {
  return {
    uploadId: entry.id,
    videoUuid: entry.videoUuid,
    bunnyVideoId: entry.bunnyVideoId,
    fileName: entry.fileName,
    fileSize: entry.fileSize,
    contentType: entry.contentType,
    source: entry.fetchClientId ? 'url' : 'file',
    startedAt: entry.startedAt,
    reconnectCount: 0,
    retryCount: 0,
    networkAttempts: 0,
    updatedAt: now,
  };
}

function withIdentity(attempt: UploadAttempt, entry: ActiveUpload): UploadAttempt {
  const next: UploadAttempt = { ...attempt };
  if (entry.videoUuid && entry.videoUuid !== attempt.videoUuid) next.videoUuid = entry.videoUuid;
  if (entry.bunnyVideoId && entry.bunnyVideoId !== attempt.bunnyVideoId) {
    next.bunnyVideoId = entry.bunnyVideoId;
  }
  if (entry.precheck && entry.precheck !== attempt.precheck) next.precheck = entry.precheck;
  // Peak, not last: tus resets its counter once a chunk commits, so the live
  // value drops back to 0 the moment the upload recovers. Keeping the high
  // water mark is what makes a survived outage legible after the fact.
  if (entry.tusRetryAttempts !== undefined && entry.tusRetryAttempts > attempt.networkAttempts) {
    next.networkAttempts = entry.tusRetryAttempts;
  }
  if (
    entry.mbpsObserved !== undefined &&
    (attempt.mbpsObservedMin === undefined || entry.mbpsObserved < attempt.mbpsObservedMin)
  ) {
    next.mbpsObservedMin = entry.mbpsObserved;
  }
  return next;
}

function withFailure(attempt: UploadAttempt, entry: ActiveUpload, now: number): UploadAttempt {
  const state = deriveUploadState({ entry });
  const failure = state.kind === 'failed' ? state : null;
  return {
    ...attempt,
    failedAt: now,
    terminal: 'failed',
    failureStage: entry.failureStage === 'processing' ? 'processing' : 'upload',
    failureReason: failure?.reason,
    failureTitle: failure?.title,
    failureMessage: failure?.message,
    failureHint: failure?.hint,
  };
}

function withPhaseTransition(
  attempt: UploadAttempt,
  prevPhase: ActiveUpload['phase'] | undefined,
  entry: ActiveUpload,
  now: number
): UploadAttempt {
  const phase = entry.phase;

  if (prevPhase === 'error' && (phase === 'preparing' || phase === 'uploading')) {
    return {
      ...attempt,
      retryCount: attempt.retryCount + 1,
      terminal: undefined,
      failedAt: undefined,
      failureStage: undefined,
      failureReason: undefined,
      failureTitle: undefined,
      failureMessage: undefined,
      failureHint: undefined,
    };
  }
  if (phase === 'uploading' && prevPhase === 'preparing' && attempt.preparedAt === undefined) {
    return { ...attempt, preparedAt: now };
  }
  if (phase === 'reconnecting') {
    return { ...attempt, reconnectCount: attempt.reconnectCount + 1 };
  }
  // Only a TUS hand-off counts as "uploaded"; url-fetch entries enter processing directly.
  const fromTus = prevPhase === 'uploading' || prevPhase === 'reconnecting';
  if (phase === 'processing' && fromTus && attempt.uploadedAt === undefined) {
    const from = attempt.preparedAt ?? attempt.startedAt;
    const seconds = (now - from) / 1000;
    const mbPerSecond = seconds > 0 ? entry.fileSize / seconds / BYTES_PER_MIB : undefined;
    return { ...attempt, uploadedAt: now, mbPerSecond };
  }
  if (phase === 'complete') {
    return { ...attempt, readyAt: now, terminal: 'ready' };
  }
  if (phase === 'error') {
    return withFailure(attempt, entry, now);
  }
  return attempt;
}

function shallowEqual(a: UploadAttempt, b: UploadAttempt): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof UploadAttempt>;
  for (const key of keys) {
    if (key === 'updatedAt') continue;
    if (a[key] !== b[key]) return false;
  }
  return true;
}

/**
 * Pure reducer: folds one upload-store entry change into its history row.
 * Returns the same `attempt` reference when nothing user-visible changed so
 * the recorder can skip the persisted write. The first change for an id
 * seeds the row from the entry — no seed call sites in the upload hook.
 */
export function applyEntryChange({ prev, next, attempt, now }: EntryChange): UploadAttempt {
  const base = withIdentity(attempt ?? seedAttempt(next, now), next);
  const phaseChanged = prev?.phase !== next.phase;
  const updated = phaseChanged ? withPhaseTransition(base, prev?.phase, next, now) : base;
  if (attempt && shallowEqual(attempt, updated)) return attempt;
  return { ...updated, updatedAt: now };
}

/**
 * Subscribes to the upload store and records phase transitions per entry.
 * The snapshot at subscribe time is the baseline (hydrated entries are not
 * replayed as fresh transitions). Returns the unsubscribe function.
 */
export function startUploadHistoryRecorder(): () => void {
  let prevUploads = useUploadStore.getState().uploads;

  return useUploadStore.subscribe((state) => {
    const nextUploads = state.uploads;
    if (nextUploads === prevUploads) return;
    const now = Date.now();

    for (const [id, entry] of nextUploads) {
      const prev = prevUploads.get(id);
      if (prev === entry) continue;
      const history = useUploadHistoryStore.getState();
      const attempt = history.attempts.find((a) => a.uploadId === id);
      const updated = applyEntryChange({ prev, next: entry, attempt, now });
      if (updated !== attempt) history.upsert(updated);
    }

    prevUploads = nextUploads;
  });
}
